import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import CpfInput from '../CpfInput';
import { maskPhone, unmask } from '../../utils/masks';
import { isValidCPF } from '../../utils/validators';
import { colaboradoresService } from '../../services/colaboradores.service';
import { cargosService } from '../../services/cargos.service';
import type { AdmissaoRetroativaPayloadContract } from '@sstexames/shared-types';
import {
  UserPlusIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useToast, ToastContainer } from '../Toast';
import { useQueryClient } from '@tanstack/react-query';

export interface ModalAdmissaoRetroativaProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number;
  cargos?: Array<{ id: number; nome: string }>;
  setores: Array<{ id: number; nome: string }>;
  userName?: string;
  onSuccess?: () => void;
}

const ESTADOS_CIVIS = [
  'Solteiro(a)',
  'Casado(a)',
  'União Estável',
  'Divorciado(a)',
  'Separado(a)',
  'Viúvo(a)',
];

const REGIMES_CONTRATACAO = [
  'CLT',
  'Estagiário',
  'Aprendiz',
  'Temporário',
  'PJ',
  'Autônomo',
];

export const ModalAdmissaoRetroativa: React.FC<ModalAdmissaoRetroativaProps> = ({
  isOpen,
  onClose,
  empresaId,
  setores,
  userName = 'Administrador do Cliente',
  onSuccess,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errorAlert, setErrorAlert] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    nome: '',
    nome_social: '',
    cpf: '',
    rg: '',
    data_nascimento: '',
    data_admissao: '',
    ultimo_periodico: '',
    cargo_id: '',
    setor_id: '',
    regime_contratacao: 'CLT',
    sexo: 'M' as 'M' | 'F',
    estado_civil: 'Solteiro(a)',
    matricula_esocial: '',
    enviar_esocial: true,
    pcd: false,
    email: '',
    telefone: '',
  });

  // Limpa alerta de erro ao reabrir/fechar modal
  useEffect(() => {
    if (!isOpen) {
      setErrorAlert(null);
    }
  }, [isOpen]);

  // Cargos específicos do setor selecionado
  const [cargosDoSetor, setCargosDoSetor] = useState<Array<{ id: number; nome: string }>>([]);
  const [loadingCargos, setLoadingCargos] = useState(false);

  // Efeito: quando o setor muda, limpa o cargo e busca os cargos vinculados a esse setor
  useEffect(() => {
    if (!formData.setor_id) {
      setCargosDoSetor([]);
      setFormData((prev) => ({ ...prev, cargo_id: '' }));
      return;
    }

    let isCancelled = false;
    setLoadingCargos(true);
    cargosService
      .findAll({ setor_id: Number(formData.setor_id), limit: 1000 })
      .then((res) => {
        if (!isCancelled) {
          setCargosDoSetor(res.data || []);
        }
      })
      .catch((err) => {
        console.error('Erro ao buscar cargos do setor:', err);
        if (!isCancelled) {
          setCargosDoSetor([]);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setLoadingCargos(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [formData.setor_id]);

  // Handler de mudança de setor (limpa explicitamente o cargo)
  const handleSetorChange = (newSetorId: string) => {
    setErrorAlert(null);
    setFormData((prev) => ({
      ...prev,
      setor_id: newSetorId,
      cargo_id: '', // Trocou setor limpa o cargo
    }));
  };

  const nowTimestamp = new Date();
  const formattedNow = `${nowTimestamp.toLocaleDateString('pt-BR')} às ${nowTimestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorAlert(null);

    if (!formData.nome.trim()) {
      const msg = 'Informe o nome completo do colaborador';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    const cleanCpf = unmask(formData.cpf);
    if (!cleanCpf) {
      const msg = 'Informe o CPF do colaborador';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }
    if (!isValidCPF(cleanCpf)) {
      const msg = 'O CPF informado é inválido. Verifique os dígitos e tente novamente.';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (!formData.data_nascimento) {
      const msg = 'Informe a data de nascimento';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (!formData.setor_id) {
      const msg = 'Selecione o setor de lotação';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (!formData.cargo_id) {
      const msg = 'Selecione o cargo/função do colaborador';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (!formData.data_admissao) {
      const msg = 'Informe a data de admissão';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    if (formData.data_admissao > todayStr) {
      const msg = 'A data de admissão deve ser retroativa (não pode ser superior à data de hoje)';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    if (formData.ultimo_periodico && formData.ultimo_periodico > todayStr) {
      const msg = 'A data do último exame periódico deve ser retroativa (não pode ser superior à data de hoje)';
      setErrorAlert(msg);
      toast.error(msg);
      return;
    }

    try {
      setSubmitting(true);
      const payload: AdmissaoRetroativaPayloadContract = {
        empresa_id: empresaId,
        nome: formData.nome.trim(),
        nome_social: formData.nome_social.trim() || undefined,
        cpf: formData.cpf,
        rg: formData.rg.trim() || undefined,
        data_nascimento: formData.data_nascimento,
        data_admissao: formData.data_admissao,
        ultimo_periodico: formData.ultimo_periodico || formData.data_admissao,
        cargo_id: Number(formData.cargo_id),
        setor_id: Number(formData.setor_id),
        regime_contratacao: formData.regime_contratacao,
        sexo: formData.sexo,
        estado_civil: formData.estado_civil || undefined,
        matricula_esocial: formData.matricula_esocial.trim() || undefined,
        enviar_esocial: formData.enviar_esocial,
        pcd: formData.pcd,
        email: formData.email.trim() || undefined,
        telefone: formData.telefone.trim() || undefined,
      };

      const res = await colaboradoresService.admissaoRetroativa(payload);
      toast.success(res.message || 'Colaborador adicionado ao quadro com sucesso! Redirecionando para Movimentações...');

      // Invalidar queries do React Query
      queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
      queryClient.invalidateQueries({ queryKey: ['organizacao'] });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string | string[] } }; message?: string };
      const rawMsg = error?.response?.data?.message || error?.message || 'Erro ao registrar admissão retroativa';
      const msg = Array.isArray(rawMsg) ? rawMsg.join('. ') : rawMsg;
      setErrorAlert(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Inclusão de Colaborador (Sentiu falta de alguém?)"
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-sm text-gray-700">
        {/* Banner de Orientação para Admissões */}
        <div className="bg-gradient-to-r from-amber-50 to-emerald-50/50 border border-amber-200/90 rounded-xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs shadow-2xs">
          <div className="flex items-center gap-2.5 text-amber-950">
            <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span>
              <strong>Atenção para Admissões:</strong> Para colaboradores que irão realizar <strong>Exame Admissional</strong>, não é necessário realizar inclusão prévia aqui! Todos os dados são informados diretamente no agendamento.
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/agendamento/cadastro?tab=individual');
            }}
            className="inline-flex items-center gap-1 font-bold text-emerald-700 hover:text-emerald-900 bg-emerald-100/70 hover:bg-emerald-200/70 px-3 py-1.5 rounded-lg whitespace-nowrap cursor-pointer flex-shrink-0 transition-colors"
          >
            <CalendarDaysIcon className="w-4 h-4 text-emerald-600" />
            <span>Agendar Admissional</span>
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Banner de Contexto Claro */}
        <div className="bg-blue-50/70 border border-blue-200 p-3 rounded-lg flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900 leading-relaxed">
            <span className="font-semibold">Autosserviço de Manutenção de Quadro:</span> Cadastre aqui colaboradores
            que já fazem parte da equipe e não constavam no sistema. Os dados de admissão e último periódico atualizarão a
            matriz de exames e a conformidade imediatamente.
          </div>
        </div>

        {/* Display Imutável de Auditoria */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="w-4 h-4 text-emerald-600" />
            <span>
              Registro imutável no sistema: <strong className="text-gray-800">{formattedNow}</strong>
            </span>
          </div>
          <span className="text-gray-500">Operador: {userName}</span>
        </div>

        {/* Banner de Erro em Destaque */}
        {errorAlert && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-xs p-3.5 rounded-lg flex items-start gap-3 animate-fadeIn">
            <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 font-medium leading-relaxed">{errorAlert}</div>
            <button
              type="button"
              onClick={() => setErrorAlert(null)}
              className="text-red-400 hover:text-red-600 transition-colors p-0.5 cursor-pointer"
              title="Fechar alerta"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Identificação Básica */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Nome Completo <span className="text-red-500 font-bold">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Ex: João da Silva Santos"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Nome Social <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="text"
              value={formData.nome_social}
              onChange={(e) => setFormData({ ...formData, nome_social: e.target.value })}
              placeholder="Nome pelo qual prefere ser chamado"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
          <CpfInput
            label="CPF"
            required
            value={formData.cpf}
            onChange={(formatted) => setFormData({ ...formData, cpf: formatted })}
            id="admissao_retroativa_cpf"
            name="cpf"
          />

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              RG <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="text"
              value={formData.rg}
              onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
              placeholder="Ex: 12.345.678-9"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Data de Nascimento <span className="text-red-500 font-bold">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.data_nascimento}
              onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Sexo <span className="text-red-500 font-bold">*</span>
            </label>
            <select
              value={formData.sexo}
              onChange={(e) => setFormData({ ...formData, sexo: e.target.value as 'M' | 'F' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
            </select>
          </div>
        </div>

        {/* Estado Civil e Matrícula */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Estado Civil
            </label>
            <select
              value={formData.estado_civil}
              onChange={(e) => setFormData({ ...formData, estado_civil: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              {ESTADOS_CIVIS.map((ec) => (
                <option key={ec} value={ec}>
                  {ec}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Matrícula eSocial / Interna <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="text"
              value={formData.matricula_esocial}
              onChange={(e) => setFormData({ ...formData, matricula_esocial: e.target.value })}
              placeholder="Ex: MAT-1024"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Regime de Contratação <span className="text-red-500 font-bold">*</span>
            </label>
            <select
              value={formData.regime_contratacao}
              onChange={(e) => setFormData({ ...formData, regime_contratacao: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              {REGIMES_CONTRATACAO.map((reg) => (
                <option key={reg} value={reg}>
                  {reg}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Setor de Lotação e Cargo Dependente */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 bg-gray-50/80 p-3.5 rounded-xl border border-gray-200">
          <div>
            <label className="block text-xs font-semibold text-gray-800 mb-1">
              Setor de Lotação <span className="text-red-500 font-bold">*</span>
            </label>
            <select
              required
              value={formData.setor_id}
              onChange={(e) => handleSetorChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              <option value="">Selecione o setor...</option>
              {setores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-gray-500 mt-1 block">
              Ao selecionar o setor, apenas os cargos vinculados serão exibidos.
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-800 mb-1">
              Cargo / Função <span className="text-red-500 font-bold">*</span>
            </label>
            <select
              required
              disabled={!formData.setor_id || loadingCargos}
              value={formData.cargo_id}
              onChange={(e) => setFormData({ ...formData, cargo_id: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg text-sm transition-colors ${
                !formData.setor_id
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
              }`}
            >
              {!formData.setor_id ? (
                <option value="">Selecione primeiro o setor acima</option>
              ) : loadingCargos ? (
                <option value="">Carregando cargos do setor...</option>
              ) : cargosDoSetor.length === 0 ? (
                <option value="">Nenhum cargo vinculado a este setor</option>
              ) : (
                <>
                  <option value="">Selecione o cargo...</option>
                  {cargosDoSetor.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </>
              )}
            </select>
            <span className="text-[11px] text-gray-500 mt-1 block">
              {!formData.setor_id ? 'Selecione o setor para habilitar a escolha do cargo.' : `${cargosDoSetor.length} cargo(s) disponível(is) neste setor.`}
            </span>
          </div>
        </div>

        {/* Flags eSocial e PCD */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50/70 p-3 rounded-lg border border-gray-200">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formData.enviar_esocial}
              onChange={(e) => setFormData({ ...formData, enviar_esocial: e.target.checked })}
              className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
            />
            <div>
              <span className="text-xs font-semibold text-gray-800 block">Enviar eventos ao eSocial</span>
              <span className="text-[11px] text-gray-500">Transmite admissão, exames e alterações aos leiautes S-2200/S-2220</span>
            </div>
          </label>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formData.pcd}
              onChange={(e) => setFormData({ ...formData, pcd: e.target.checked })}
              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
            />
            <div>
              <span className="text-xs font-semibold text-gray-800 block">Pessoa com Deficiência (PCD)</span>
              <span className="text-[11px] text-gray-500">Aplica cota legal e especificidades ergonômicas de conformidade</span>
            </div>
          </label>
        </div>

        {/* Datas de Admissão e Periódico */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 bg-amber-50/70 p-3.5 rounded-xl border border-amber-200">
          <div>
            <label className="block text-xs font-semibold text-amber-950 mb-1">
              Data de Admissão <span className="text-red-500 font-bold">*</span>
            </label>
            <input
              type="date"
              required
              max={new Date().toISOString().split('T')[0]}
              value={formData.data_admissao}
              onChange={(e) => setFormData({ ...formData, data_admissao: e.target.value })}
              className="w-full px-3 py-2 border border-amber-300 bg-white rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
            />
            <span className="text-[11px] text-amber-800 mt-1 block font-medium">Obrigatório data retroativa (até hoje).</span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-amber-950 mb-1">
              Data do Último Exame Periódico <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="date"
              max={new Date().toISOString().split('T')[0]}
              value={formData.ultimo_periodico}
              onChange={(e) => setFormData({ ...formData, ultimo_periodico: e.target.value })}
              className="w-full px-3 py-2 border border-amber-300 bg-white rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
            />
            <span className="text-[11px] text-amber-800 mt-1 block">
              Data retroativa (até hoje): se não preenchido, considera a data de admissão.
            </span>
          </div>
        </div>

        {/* Contato Opcional */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              E-mail do Colaborador <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="colaborador@empresa.com.br"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Telefone / WhatsApp <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="text"
              maxLength={15}
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: maskPhone(e.target.value) })}
              placeholder="(00) 00000-0000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
            />
          </div>
        </div>

        {/* Rodapé com Botões */}
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm flex items-center gap-2 transition-colors disabled:opacity-50 cursor-pointer"
          >
            <UserPlusIcon className="w-4 h-4" />
            {submitting ? 'Salvando...' : 'Adicionar ao Quadro'}
          </button>
        </div>
      </form>
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </Modal>
  );
};

export default ModalAdmissaoRetroativa;
